import { useCallback, useEffect, useRef, useState } from "react";

import Bugsnag from "@bugsnag/js";
import { saveAs } from "file-saver";

import { SerializedChecklistDocument } from "../lib/model/checklistSerialized";
import { PDFWorkerRequest, PDFWorkerResponse } from "../workers/pdf.worker.types";

// Timeout for PDF generation (30 seconds)
const PDF_GENERATION_TIMEOUT_MS = 30000;

export interface UseDownloadPDFResult {
  download: (document: SerializedChecklistDocument) => Promise<void>;
  isLoading: boolean;
}

export const useDownloadPDF = (): UseDownloadPDFResult => {
  const [isLoading, setIsLoading] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const pendingResolveRef = useRef<((blob: Blob) => void) | null>(null);
  const pendingRejectRef = useRef<((error: Error) => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to clear pending state
  const clearPendingState = useCallback(() => {
    pendingResolveRef.current = null;
    pendingRejectRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Create worker on mount
    const worker = new Worker(
      new URL("../workers/pdf.worker.tsx", import.meta.url)
    );

    worker.onmessage = (event: MessageEvent<PDFWorkerResponse>) => {
      const { type, blob, error, stack } = event.data;

      if (type === "success" && blob && pendingResolveRef.current) {
        pendingResolveRef.current(blob);
      } else if (type === "error" && pendingRejectRef.current) {
        const err = new Error(error ?? "Unknown error");
        if (stack) {
          err.stack = stack;
        }
        Bugsnag.notify(err);
        pendingRejectRef.current(err);
      }

      clearPendingState();
    };

    worker.onerror = (event) => {
      console.error("PDF Worker error:", event);
      const err = new Error(event.message || "Worker error");
      Bugsnag.notify(err, (bugsnagEvent) => {
        bugsnagEvent.context = "PDF Worker initialization";
      });
      if (pendingRejectRef.current) {
        pendingRejectRef.current(err);
      }
      clearPendingState();
    };

    workerRef.current = worker;

    return () => {
      worker.terminate();
      clearPendingState();
    };
  }, [clearPendingState]);

  const download = useCallback(async (document: SerializedChecklistDocument) => {
    const worker = workerRef.current;
    if (!worker) {
      const err = new Error("PDF worker not initialized");
      Bugsnag.notify(err);
      throw err;
    }

    setIsLoading(true);

    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        pendingResolveRef.current = resolve;
        pendingRejectRef.current = reject;

        // Set up timeout
        timeoutRef.current = setTimeout(() => {
          const err = new Error(
            `PDF generation timed out after ${PDF_GENERATION_TIMEOUT_MS / 1000} seconds`
          );
          Bugsnag.notify(err, (event) => {
            event.context = "PDF Worker timeout";
            event.addMetadata("document", {
              title: document.title,
              checklistCount: document.contents.checklists.length,
            });
          });
          clearPendingState();
          reject(err);
        }, PDF_GENERATION_TIMEOUT_MS);

        const request: PDFWorkerRequest = { type: "generate", document };
        worker.postMessage(request);
      });

      const documentTitle = `${document.title || "Untitled"}.pdf`;
      saveAs(blob, documentTitle);
    } finally {
      setIsLoading(false);
    }
  }, [clearPendingState]);

  return { download, isLoading };
};
